import * as lucideIcons from 'lucide-vue-next'
import iconPaths from '../../lib/internal-icons'

type CapitalizeWord<S extends string> = S extends `${infer First}${infer Rest}`
  ? `${Uppercase<First>}${Rest}`
  : S

export type KebabToPascal<S extends string> =
  S extends `${infer Head}-${infer Tail}`
    ? `${CapitalizeWord<Head>}${KebabToPascal<CapitalizeWord<Tail>>}`
    : CapitalizeWord<S>

export type PascalToKebab<S extends string> =
  S extends `${infer First}${infer Rest}`
    ? `${LowercaseChar<First>}${KebabHelper<Rest>}`
    : S

type KebabHelper<S extends string> = S extends `${infer First}${infer Rest}`
  ? First extends AlphabethicChar
    ? First extends Lowercase<First>
      ? `${First}${KebabHelper<Rest>}`
      : `-${Lowercase<First>}${KebabHelper<Rest>}`
    : `-${First}${KebabHelper<Rest>}`
  : S

type AlphabethicChar =
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r'
  | 's'
  | 't'
  | 'u'
  | 'v'
  | 'w'
  | 'x'
  | 'y'
  | 'z'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z'

type LowercaseChar<C extends string> = C extends AlphabethicChar
  ? Lowercase<C>
  : C

export type LucideIconComponentName = Exclude<
  keyof typeof lucideIcons,
  'Icon' | 'createLucideIcon' | 'icons'
>

export type LucideIconName = PascalToKebab<LucideIconComponentName>

export type InternalIconName = keyof typeof iconPaths

export type IconName = LucideIconName | InternalIconName

export const kebabToPascal = <S extends string>(str: S): KebabToPascal<S> => {
  const camelCase = str.replace(/-([a-zA-Z0-9])/g, (_, char) =>
    typeof char === 'string' ? char.toUpperCase() : '',
  )
  return (camelCase.charAt(0).toUpperCase() +
    camelCase.slice(1)) as KebabToPascal<S>
}

export const isLucideIconName = (
  iconName: IconName,
): iconName is LucideIconName => !(iconName in iconPaths)
